import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { buildApiUrl } from '../../utils/apiBase.js';
import { AdminFormPageShimmer } from '../common/Shimmer.jsx';
import EmailTemplateEditor from './EmailTemplateEditor.jsx';
import { useScreenProbe } from '../../utils/screenTiming.js';
import { can } from '../../config/adminPermissions.js';
import './EmailSettings.css';

const DEFAULT_CLINIC_ID = '3484438104e108b5c45815faa9fbcba6';

const GROUP_META = [
  { key: 'account', title: 'アカウント・認証', desc: 'UserHub 管轄 — 認証・権限関連（プレビュー/送信とも UserHub）' },
  { key: 'license', title: 'ライセンス・契約', desc: 'UserHub 管轄 — ライセンス・解約（プレビュー/送信とも UserHub）' },
  { key: 'billing', title: '決済・請求', desc: 'UserHub 管轄 — Stripe・決済（プレビュー/送信とも UserHub）' },
  { key: 'contact', title: 'お問い合わせ', desc: 'UserHub 管轄 — 問い合わせフォーム（プレビュー/送信とも UserHub）' },
  { key: 'announce', title: 'お知らせ・運用', desc: 'AdminHub 管轄 — 一斉送信・規約改定（プレビュー/送信とも AdminHub）' },
  { key: 'referral', title: '招待・紹介特典', desc: 'UserHub 管轄 — 招待コード・特典（プレビュー/送信とも UserHub）' },
];

function isUserHubTemplate(t) {
  return t?.hub === 'userhub';
}

function getUserHubSendBlockReason({ userHubReachable, userHubSendAvailable, userHubSmtpConfigured }) {
  if (!userHubReachable) return 'UserHub 未接続のためテスト送信できません';
  if (userHubSendAvailable === false) {
    return '本番では UserHub テスト送信 API が非公開です（staging / ローカルのみ）';
  }
  if (!userHubSmtpConfigured) return 'UserHub のメール送信基盤が未設定です';
  return null;
}

const EmailSettings = ({ user }) => {
  const probe = useScreenProbe('email-settings');
  const canSend = can(user, 'email:write');
  const [templates, setTemplates] = useState([]);
  const [smtpConfigured, setSmtpConfigured] = useState(null);
  const [userHubReachable, setUserHubReachable] = useState(null);
  const [userHubInternalConfigured, setUserHubInternalConfigured] = useState(null);
  const [userHubFetchError, setUserHubFetchError] = useState(null);
  const [userHubSmtpConfigured, setUserHubSmtpConfigured] = useState(null);
  const [userHubSendAvailable, setUserHubSendAvailable] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  const [mode, setMode] = useState('e2e');
  const [clinicId, setClinicId] = useState(DEFAULT_CLINIC_ID);
  const [clinicInfo, setClinicInfo] = useState(null);
  const [clinicLoading, setClinicLoading] = useState(false);
  const [toEmail, setToEmail] = useState('');
  const [toEmailOverride, setToEmailOverride] = useState('');
  const [overrideTo, setOverrideTo] = useState('');

  const [sending, setSending] = useState(null);
  const [results, setResults] = useState({});

  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewKey, setPreviewKey] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewMeta, setPreviewMeta] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewSubject, setPreviewSubject] = useState('');
  const [previewMessage, setPreviewMessage] = useState(null);
  const [previewRenderedBy, setPreviewRenderedBy] = useState(null);

  const fetchTemplates = useCallback(async () => {
    probe.start('load');
    try {
      const [listRes, devRes] = await Promise.all([
        fetch(buildApiUrl('/api/admin/email-templates'), { credentials: 'include' }),
        fetch(buildApiUrl('/api/admin/dev-test/email/templates'), { credentials: 'include' }),
      ]);
      const listData = await listRes.json();
      const devData = await devRes.json();

      setTemplates(devData.templates || listData.templates || []);
      setSmtpConfigured(devData.smtpConfigured);
      setUserHubReachable(devData.userHubReachable);
      setUserHubInternalConfigured(devData.userHubInternalConfigured);
      setUserHubFetchError(devData.userHubFetchError || null);
      setUserHubSmtpConfigured(devData.userHubSmtpConfigured);
      setUserHubSendAvailable(devData.userHubSendAvailable);

      const ov = devData.overrideTo || '';
      setOverrideTo(ov);
      setToEmail(ov);
      setToEmailOverride(ov);
      probe.end('load', 'ok');
      probe.unlock();
    } catch (err) {
      probe.end('load', `error: ${err?.message || 'unknown'}`);
      console.error('[EmailSettings] 取得失敗:', err);
    } finally {
      setLoading(false);
    }
  }, [probe]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const fetchClinicInfo = useCallback(async () => {
    if (!canSend || !clinicId.trim()) return;
    setClinicLoading(true);
    setClinicInfo(null);
    try {
      const res = await fetch(
        buildApiUrl(`/api/admin/dev-test/email/clinic-info?clinicId=${encodeURIComponent(clinicId.trim())}`),
        { credentials: 'include' }
      );
      setClinicInfo(await res.json());
    } catch (err) {
      setClinicInfo({ error: err.message });
    } finally {
      setClinicLoading(false);
    }
  }, [clinicId, canSend]);

  useEffect(() => {
    if (mode === 'e2e' && clinicId.trim()) fetchClinicInfo();
  }, [mode, clinicId, fetchClinicInfo]);

  const canSendTemplate = useCallback((t) => {
    if (t.hub === 'userhub') {
      if (getUserHubSendBlockReason({ userHubReachable, userHubSendAvailable, userHubSmtpConfigured })) {
        return false;
      }
    } else if (!smtpConfigured) {
      return false;
    }
    if (mode === 'e2e') {
      return clinicId.trim() && (toEmailOverride.trim() || clinicInfo?.email);
    }
    return Boolean(toEmail.trim());
  }, [smtpConfigured, userHubReachable, userHubSmtpConfigured, userHubSendAvailable, mode, clinicId, toEmailOverride, clinicInfo, toEmail]);

  const sendBlockTitle = useCallback((t) => {
    if (sending !== null) return '送信処理中です';
    if (isUserHubTemplate(t)) {
      const uh = getUserHubSendBlockReason({
        userHubReachable,
        userHubSendAvailable,
        userHubSmtpConfigured,
      });
      if (uh) return uh;
    } else if (!smtpConfigured) {
      return 'AdminHub のメール送信基盤が未設定です';
    }
    if (mode === 'e2e') {
      if (!clinicId.trim()) return 'クリニック ID を指定してください';
      if (!toEmailOverride.trim() && !clinicInfo?.email) return '送信先メールがありません（上書きまたはクリニックメール）';
    } else if (!toEmail.trim()) {
      return '送信先メールアドレスを指定してください';
    }
    return 'テスト送信';
  }, [
    sending, userHubReachable, userHubSendAvailable, userHubSmtpConfigured,
    smtpConfigured, mode, clinicId, toEmailOverride, clinicInfo, toEmail,
  ]);

  const canPreviewTemplate = useCallback((t) => {
    if (!isUserHubTemplate(t)) return true;
    return Boolean(userHubInternalConfigured && userHubReachable);
  }, [userHubInternalConfigured, userHubReachable]);

  const handleSend = useCallback(async (templateKey, e) => {
    e?.stopPropagation();
    if (!canSend) return;
    setSending(templateKey);
    setResults((prev) => ({ ...prev, [templateKey]: null }));
    try {
      const url = mode === 'e2e'
        ? '/api/admin/dev-test/email/send-e2e'
        : '/api/admin/dev-test/email/send';
      const body = mode === 'e2e'
        ? { templateKey, clinicId: clinicId.trim(), ...(toEmailOverride.trim() ? { toEmailOverride: toEmailOverride.trim() } : {}) }
        : { templateKey, toEmail: toEmail.trim() };

      const res = await fetch(buildApiUrl(url), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(body),
      });
      const data = await res.json();
      setResults((prev) => ({
        ...prev,
        [templateKey]: {
          ok: res.ok && data.success,
          message: data.success
            ? `送信成功 (${data.via || data.proxiedVia || 'adminhub'}) → ${data.sentTo || data.to || ''}`
            : data.message || data.reason || '送信失敗',
        },
      }));
    } catch (err) {
      setResults((prev) => ({
        ...prev,
        [templateKey]: { ok: false, message: `ネットワークエラー: ${err.message}` },
      }));
    } finally {
      setSending(null);
    }
  }, [mode, clinicId, toEmailOverride, toEmail, canSend]);

  const loadPreview = useCallback(async (key) => {
    setPreviewLoading(true);
    setPreviewMessage(null);
    try {
      const [metaRes, previewRes] = await Promise.all([
        fetch(buildApiUrl(`/api/admin/email-templates/${key}`), { credentials: 'include' }),
        fetch(buildApiUrl(`/api/admin/email-templates/${key}/preview`), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({}),
        }),
      ]);
      const metaData = await metaRes.json();
      const previewData = await previewRes.json();
      if (!metaRes.ok) throw new Error(metaData.message || 'テンプレート取得失敗');
      if (!previewRes.ok) throw new Error(previewData.message || 'プレビュー取得失敗');
      setPreviewMeta(metaData.template);
      if (previewData.preview) {
        setPreviewHtml(previewData.preview.html);
        setPreviewSubject(previewData.preview.subject);
        setPreviewRenderedBy(previewData.preview.renderedBy || previewData.preview.hub || null);
      }
    } catch (err) {
      setPreviewMessage({ type: 'error', text: err.message });
      setPreviewHtml(`<p style="padding:24px;color:#b91c1c;">プレビュー取得失敗: ${err.message}</p>`);
      setPreviewRenderedBy(null);
    } finally {
      setPreviewLoading(false);
    }
  }, []);

  const openPreview = useCallback(async (key, template) => {
    if (template && !canPreviewTemplate(template)) {
      setPreviewKey(key);
      setPreviewOpen(true);
      setPreviewMeta(template);
      setPreviewHtml('');
      setPreviewSubject('');
      setPreviewRenderedBy(null);
      setPreviewMessage({
        type: 'error',
        text: 'UserHub に接続できないため、UserHub 管轄テンプレートのプレビューを表示できません。接続設定を確認してください。',
      });
      return;
    }
    setPreviewKey(key);
    setPreviewOpen(true);
    setPreviewHtml('');
    setPreviewSubject('');
    setPreviewMeta(null);
    setPreviewRenderedBy(null);
    await loadPreview(key);
  }, [loadPreview, canPreviewTemplate]);

  const closePreview = () => {
    setPreviewOpen(false);
    setPreviewKey(null);
    setPreviewMeta(null);
    setPreviewHtml('');
    setPreviewSubject('');
    setPreviewMessage(null);
    setPreviewRenderedBy(null);
  };

  const filteredTemplates = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return templates;
    return templates.filter(
      (t) => t.label.toLowerCase().includes(q)
        || t.key.toLowerCase().includes(q)
        || t.group.toLowerCase().includes(q)
        || (t.hub || '').toLowerCase().includes(q),
    );
  }, [templates, searchQuery]);

  const groupedTemplates = useMemo(() => GROUP_META.map(({ key, title, desc }) => ({
    key,
    title,
    desc,
    items: filteredTemplates.filter((t) => t.group === key),
  })).filter((g) => g.items.length > 0), [filteredTemplates]);

  const userHubTemplateCount = useMemo(
    () => templates.filter(isUserHubTemplate).length,
    [templates],
  );

  if (loading) {
    return <AdminFormPageShimmer fields={4} />;
  }

  return (
    <div className="es-wrapper">
      <div className="card es-status-card">
        <div className="es-status-header">
          <div>
            <h2 className="es-section-title">メール送信基盤</h2>
            <p className="es-status-sub">
              全 {templates.length} テンプレート（UserHub 管轄 {userHubTemplateCount} / AdminHub 管轄 {templates.length - userHubTemplateCount}）— 文面はコード正本（編集不可）
            </p>
          </div>
        </div>
        <div className="es-status-grid">
          <div className="es-status-item">
            <span className="es-status-label">AdminHub SMTP</span>
            <span className={`es-badge ${smtpConfigured ? 'ok' : 'ng'}`}>{smtpConfigured ? '設定済み' : '未設定'}</span>
          </div>
          <div className="es-status-item">
            <span className="es-status-label">UserHub 接続</span>
            <span className={`es-badge ${userHubReachable ? 'ok' : userHubInternalConfigured ? 'ng' : 'off'}`}>
              {userHubReachable ? '接続可' : userHubInternalConfigured ? '接続失敗' : '未設定'}
            </span>
          </div>
          <div className="es-status-item">
            <span className="es-status-label">UserHub テスト送信</span>
            <span className={`es-badge ${userHubSendAvailable ? 'ok' : userHubReachable ? 'warn' : 'off'}`}>
              {userHubSendAvailable ? '利用可' : userHubReachable ? '本番非公開' : '—'}
            </span>
          </div>
          <div className="es-status-item">
            <span className="es-status-label">文面ソース</span>
            <span className="es-badge info">コード正本</span>
          </div>
          <div className="es-status-item">
            <span className="es-status-label">オーバーライド</span>
            <span className={`es-badge ${overrideTo ? 'warn' : 'off'}`}>{overrideTo || 'なし'}</span>
          </div>
        </div>
        <p className="es-note info">
          UserHub 管轄テンプレートのプレビューは <strong>UserHub の実送信コード</strong>を internal API 経由で取得します（AdminHub ローカル描画ではありません）。
        </p>
        {userHubFetchError && (
          <p className="es-note warn">
            UserHub 未接続のため、UserHub 管轄の<strong>プレビューとテスト送信</strong>は利用できません（{userHubFetchError}）。AdminHub 管轄は利用可能です。
          </p>
        )}
        {userHubReachable && userHubSendAvailable === false && (
          <p className="es-note warn">
            UserHub プレビューは利用できます。本番 UserHub では<strong>テスト送信 API は非公開</strong>のため、UserHub 管轄のテスト送信はできません（staging / ローカルのみ）。
          </p>
        )}
      </div>

      <div className="card es-config-card">
        <div className="es-config-head">
          <h2 className="es-section-title">{canSend ? 'テスト送信' : 'テンプレート'}</h2>
          <input
            type="search"
            className="es-template-search"
            placeholder="テンプレート・管轄を検索…"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>
        {canSend && (
        <>
        <div className="es-mode-switch">
          <button type="button" className={`es-mode-btn ${mode === 'e2e' ? 'active' : ''}`} onClick={() => { setMode('e2e'); setResults({}); }}>
            E2E（実経路）
          </button>
          <button type="button" className={`es-mode-btn ${mode === 'direct' ? 'active' : ''}`} onClick={() => { setMode('direct'); setResults({}); }}>
            直接送信
          </button>
        </div>
        {mode === 'e2e' ? (
          <div className="es-fields">
            <div className="es-field">
              <label htmlFor="es-clinic-id">クリニック ID</label>
              <div className="es-field-row">
                <input id="es-clinic-id" type="text" value={clinicId} onChange={(e) => setClinicId(e.target.value)} />
                <button type="button" className="btn btn-secondary btn-sm" onClick={fetchClinicInfo} disabled={clinicLoading}>
                  {clinicLoading ? '…' : '取得'}
                </button>
              </div>
            </div>
            {clinicInfo && !clinicInfo.error && (
              <p className="es-clinic-info">{clinicInfo.clinicName} — {clinicInfo.email || 'メール未設定'}</p>
            )}
            <div className="es-field">
              <label htmlFor="es-override">送信先（任意上書き）</label>
              <input id="es-override" type="email" value={toEmailOverride} onChange={(e) => setToEmailOverride(e.target.value)} placeholder="空欄でクリニック登録メール" />
            </div>
          </div>
        ) : (
          <div className="es-field">
            <label htmlFor="es-to">送信先メール</label>
            <input id="es-to" type="email" value={toEmail} onChange={(e) => setToEmail(e.target.value)} />
          </div>
        )}
        </>
        )}
      </div>

      {groupedTemplates.map(({ key, title, desc, items }) => (
        <div key={key} className="card es-group-card">
          <div className="es-group-head">
            <div>
              <h3 className="es-group-title">{title}</h3>
              <p className="es-group-desc">{desc}</p>
            </div>
            <span className="es-group-count">{items.length} 件</span>
          </div>
          <div className="es-card-grid">
            {items.map((t) => {
              const sendOk = canSendTemplate(t);
              const previewOk = canPreviewTemplate(t);
              const userHub = isUserHubTemplate(t);
              return (
                <div key={t.key} className={`es-template-card ${userHub ? 'es-card-userhub' : 'es-card-adminhub'}`}>
                  <button
                    type="button"
                    className="es-card-main"
                    onClick={() => openPreview(t.key, t)}
                    title={previewOk ? '実送信同一プレビューを開く' : 'UserHub 未接続のためプレビュー不可'}
                  >
                    <div className="es-card-top">
                      <span className="es-card-label">{t.label}</span>
                      <span className="es-edit-hint">{previewOk ? 'クリックでプレビュー →' : 'UserHub 未接続'}</span>
                    </div>
                    <div className="es-card-badges">
                      <span className={`es-hub-badge ${t.hub}`}>{userHub ? 'UserHub 管轄' : 'AdminHub 管轄'}</span>
                      <span className={`es-render-mini ${userHub ? 'userhub' : 'admin'}`}>
                        {userHub ? '描画: UserHub' : '描画: AdminHub'}
                      </span>
                      <span className="es-kind-mini">{t.kind === 'full-html' ? 'HTML' : 'Hub'}</span>
                    </div>
                    <code className="es-card-key">{t.key}</code>
                  </button>
                  <div className="es-card-footer">
                    {canSend && (
                    <span
                      className="es-btn-send-wrap"
                      title={sendBlockTitle(t)}
                    >
                      <button
                        type="button"
                        className="btn btn-primary btn-sm es-btn-send"
                        disabled={!sendOk || sending !== null}
                        aria-disabled={!sendOk || sending !== null}
                        onClick={(e) => handleSend(t.key, e)}
                      >
                        {sending === t.key ? '送信中…' : '送信'}
                      </button>
                    </span>
                    )}
                  </div>
                  {results[t.key] && (
                    <div className={`es-result ${results[t.key].ok ? 'ok' : 'ng'}`}>{results[t.key].message}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      <EmailTemplateEditor
        open={previewOpen}
        templateKey={previewKey}
        meta={previewMeta}
        loading={previewLoading && !previewHtml}
        message={previewMessage}
        previewHtml={previewHtml}
        previewSubject={previewSubject}
        previewLoading={previewLoading}
        previewRenderedBy={previewRenderedBy}
        onClose={closePreview}
        onRefreshPreview={loadPreview}
      />
    </div>
  );
};

export default EmailSettings;
